import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";
import { DotLegalDialog, DotLegalOverflowMenuItem, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { useEditLegalEntityDocumentHook } from "./EditLegalEntityDocumentDialog.hooks";
import DocumentVersionTable from "../../../documents/documentVersionTable/DocumentVersionTable";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { StepTypeEnum } from "../../../processingActivity/ProcessingActivity.types";
import GroupEntitySelector from "@common/components/GroupEntitySelector";
import AttachDocumentToSharings from "../AttachDocumentToSharings";
import { EntityType } from "../../../common/Common.types";
import { DocumentTypeUsageType } from "../../../masterData/documentType/documentTypes/DocumentTypes.types";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection.tsx";

export interface IEditLegalEntityDocumentDialogProps {
    documentId: string;
    legalEntityId: string;
    legalEntityName?: string;
    isGroupEntity: boolean;
    onCloseDialog: () => void;
    isExtended: boolean;
    onSave?: () => void;
    stepType?: StepTypeEnum;
    overFlowMenu?: Array<DotLegalOverflowMenuItem>;
    documentTypeUsageType: DocumentTypeUsageType;
}

function EditLegalEntityDocumentDialog(props: IEditLegalEntityDocumentDialogProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const {
        document,
        setDocument,
        documentTypes,
        inProgress,
        validation,
        validateDocumentForm,
        userOptions,
        userOptionsIsLoading,
        isLoading,
        documentsUploading,
        setDocumentsUploading,
        documentStatus,
        onGroupEntityAccessIdsChange,
        onAllGroupEntitiesChange,
        gdpo,
    } = useEditLegalEntityDocumentHook(props);

    validation?.check(document);

    return (
        <DotLegalDialog
            header={translateString("editDocument")}
            size="md"
            isLoading={false}
            buttonOkText={translateString("save")}
            open
            onDialogClose={() => props.onCloseDialog()}
            onOkClick={() => validateDocumentForm()}
            inProgress={inProgress}
            okButtonDisabled={documentsUploading}
            disableBackdropOnCloseIfValueChange={document}
            overFlowMenu={props.overFlowMenu}
        >
            <Grid container columnSpacing={2}>
                <Grid size={{ md: 6 }}>
                    <DotLegalTextField
                        label={translateString("name")}
                        value={document?.name!}
                        debounce={false}
                        onChange={(text) => {
                            let tempModel = { ...document! };
                            tempModel.name = text;
                            setDocument(tempModel);
                        }}
                        errorText={validation?.getErrorsForDisplay("name")}
                        isLoading={isLoading}
                    />
                </Grid>

                <Grid size={{ md: 6 }}>
                    <DotLegalSelect
                        selectedItem={document?.documentTypeId ? document?.documentTypeId.toString() : undefined}
                        options={documentTypes}
                        placeholder={translateString("type")}
                        label={translateString("type")}
                        disableClearable
                        onChange={(documentTypeId) => {
                            let tempModel = { ...document! };
                            tempModel.documentTypeId = documentTypeId ?? undefined;
                            tempModel.addDocumentToSharings = [];
                            setDocument(tempModel);
                        }}
                        noOptionsLabel={translateString("noOptions")}
                        isLoading={isLoading}
                    />
                </Grid>

                {props.isExtended && (
                    <Grid size={{ md: 6 }}>
                        <DotLegalSelect
                            selectedItem={document?.ownerId}
                            options={userOptions}
                            placeholder={translateString("owner")}
                            label={translateString("owner")}
                            onChange={(owner) => {
                                let tempModel = { ...document! };
                                tempModel.ownerId = owner;

                                setDocument(tempModel);
                            }}
                            isLoading={userOptionsIsLoading}
                            noOptionsLabel={translateString("noOptions")}
                        />
                    </Grid>
                )}

                <Grid size={{ md: 6 }}>
                    <DotLegalSelect
                        selectedItem={document?.status ? document?.status.toString() : undefined}
                        options={documentStatus}
                        placeholder={translateString("status")}
                        label={translateString("status")}
                        disableClearable
                        onChange={(status) => {
                            let tempModel = { ...document! };
                            tempModel.status = Number(status);
                            setDocument(tempModel);
                        }}
                        noOptionsLabel={translateString("noOptions")}
                        disabled={!permissions.canSetDocumentStatus}
                        isLoading={isLoading}
                    />
                </Grid>
            </Grid>
            {props.isExtended && (
                <AttachDocumentToSharings
                    documentTypes={documentTypes ?? []}
                    document={document}
                    onChange={(documentUsageType) => {
                        let tempModel = { ...document! };
                        if (document?.addDocumentToSharings.includes(documentUsageType)) {
                            tempModel.addDocumentToSharings = tempModel.addDocumentToSharings.filter((usageType) => usageType !== documentUsageType);
                        } else {
                            tempModel.addDocumentToSharings.push(documentUsageType);
                        }
                        setDocument(tempModel);
                    }}
                />
            )}
            <Box marginTop={2}>
                <DocumentVersionTable
                    entityId={props.legalEntityId}
                    entityType={EntityType.LegalEntityDocument}
                    entityName={props.legalEntityName}
                    documentId={props.documentId}
                    documentsUploading={documentsUploading}
                    setDocumentsUploading={setDocumentsUploading}
                />
            </Box>
            {!props.isGroupEntity && (
                <GroupEntitySelector
                    switchLabel={translateString("globalDocument")}
                    explanation={translateString("globalDocumentTooltip")}
                    selectedUserGroupCompanies={document?.groupEntityAccessIds?.map((id) => ({ userId: null, groupCompanyId: id }))}
                    onGroupCompaniesChange={onGroupEntityAccessIdsChange}
                    appliesToAllGroupEntities={document?.allGroupEntities ?? false}
                    onAppliesToAllGroupEntitiesChange={onAllGroupEntitiesChange}
                    toggleDisabled={!gdpo}
                    visibleToNonGdpo
                    showError={!!validation?.getErrorsForDisplay("groupEntityAccessIds")}
                    errorText={validation?.getErrorsForDisplay("groupEntityAccessIds")}
                />
            )}
        </DotLegalDialog>
    );
}

export default EditLegalEntityDocumentDialog;
